/* eslint-disable @typescript-eslint/no-use-before-define */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { MiciPhase } from '../../../../services/types/MiciCases';
import { customDateStr } from '../../../../shared/misc/utils';

interface Props {
  currentPhase: string;
  phases: MiciPhase[];
}

const MiciTimeline: React.FC<Props> = (props) => {
  const { currentPhase, phases } = props;
  const { t } = useTranslation();

  const {
    currentPhaseIndex,
    currentMilestonePhaseIndex,
    currentMilestonePhase,
    currentMilestoneIndex,
    currentMilestone,
    lastPhaseName,
    outStage,
  } = initData(phases, currentPhase);

  return (
    <div className="mici-timeline-wrapper table-responsive mb-4">
      <div className="mici-timeline">
        {phases.map((phase, phaseIndex) => {
          const isCurrentPhase = phaseIndex === currentPhaseIndex;
          const isPreviousPhase = phaseIndex < currentMilestonePhaseIndex;
          const isPhaseInCurrentMilestone =
            currentMilestonePhase && phase.code === currentMilestonePhase.code;
          return (
            <Fragment key={`mici_timeline_step_${phase.code}`}>
              <div
                className={`step-item ${classnames({
                  current: isPhaseInCurrentMilestone,
                  previous: isPreviousPhase,
                })}`}
                style={{
                  flexGrow:
                    phase.milestones.filter((m) => m.showInTimeline).length > 1 ? 3 : undefined,
                }}
              >
                <div className={`name${isCurrentPhase ? ' current' : ''}`}>
                  {t(`miciCaseStatusCodes.${phase.code}`, phase.code)}
                  <i>{phase.status}</i>
                </div>
                <div className="version-container">
                  {phase.milestones
                    .filter((m) => m.showInTimeline)
                    .map((milestone, mIndex) => {
                      const isPreviousMilestone = mIndex < currentMilestoneIndex;
                      return (
                        <div
                          key={`milestone_item_${phase.code}_${milestone.code}`}
                          className={`version-item ${classnames({
                            current: currentMilestone && milestone.code === currentMilestone.code,
                            previous: isPreviousMilestone,
                            last: phase.code === lastPhaseName,
                          })}`}
                        >
                          <div className="flow-line" />
                          <div className={`flow-ball ${outStage ? 'not-current' : 'current'}`} />
                          <div className="version-line" />
                          <div className="version-label">
                            {t(`miciCaseMilestoneNames.${milestone.code}`, milestone.code)}
                            <div className="version-label__date">
                              <i>{customDateStr(milestone.date, 'yyyy-MM-dd')}</i>
                            </div>
                          </div>
                          <div className={`half-container ${outStage ? 'not-current' : 'current'}`}>
                            <div className="half-flow-line " />
                          </div>
                        </div>
                      );
                    })}
                  {phase.milestones.length === 0 && (
                    <div className="version-item">
                      <div className="flow-line" />
                    </div>
                  )}
                  {phaseIndex === phases.length - 1 && (
                    <div className="version-item icon-container last">
                      <div className="icon">
                        <FontAwesomeIcon icon="check" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const initData = (phases: MiciPhase[], currentPhase: string) => {
  const currentPhaseIndex = phases.map((x) => x.code).indexOf(currentPhase);

  const allMilestones = phases.map((x) => x.milestones).flat();
  const milestones = allMilestones.filter((m) => m.showInTimeline);

  milestones.reverse();
  let currentMilestone = milestones.find((x) => x.date !== null);

  const lastPhaseName = phases[phases.length - 1].code;

  let currentMilestonePhase = phases.find((x) =>
    x.milestones.find((m) => currentMilestone && m.code === currentMilestone.code)
  );
  let currentMilestonePhaseIndex = currentMilestonePhase
    ? phases.map((x) => x.code).indexOf(currentMilestonePhase.code)
    : -1;

  let outStage = false;
  if (currentPhaseIndex > currentMilestonePhaseIndex) {
    currentMilestonePhase = phases[currentPhaseIndex];

    if (currentMilestonePhase) {
      const phaseMilestones = currentMilestonePhase.milestones;

      if (phaseMilestones) {
        currentMilestone = phaseMilestones.find((m) => m.showInTimeline);
      }
      currentMilestonePhase = phases[currentPhaseIndex];
      currentMilestonePhaseIndex = currentPhaseIndex;

      outStage = true;
    }
  }

  const currentMilestoneIndex =
    currentMilestonePhase && currentMilestone
      ? currentMilestonePhase.milestones
          .filter((m) => m.showInTimeline)
          .map((x) => x.code)
          .indexOf(currentMilestone.code)
      : -1;

  return {
    currentPhaseIndex,
    currentMilestonePhaseIndex,
    currentMilestonePhase,
    currentMilestoneIndex,
    currentMilestone,
    lastPhaseName,
    outStage,
  };
};

export default MiciTimeline;
